import React, { useState } from 'react';
import { useStytchB2BClient } from '@stytch/react/b2b';
import adminService from '../services/AdminService';

/**
 * NewUserOnboarding - Profile completion for JIT-provisioned users
 *
 * When a user authenticates via Stytch JIT provisioning, they exist in Stytch
 * but not in our DynamoDB. This component collects their profile info and
 * creates them in our database with pending_approval status.
 */
const NewUserOnboarding = ({ email, eulaVersion, onComplete }) => {
  const stytch = useStytchB2BClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Log eulaVersion on mount and changes
  console.log('🔍 NEW-USER-ONBOARDING: Rendered with eulaVersion:', eulaVersion);
  if (!eulaVersion) {
    console.error('❌ NEW-USER-ONBOARDING: eulaVersion prop is missing! EULA will not be saved during profile completion.');
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Get JWT token from Stytch session and set on adminService
      const jwt = stytch?.session.getTokens()?.session_jwt;
      if (jwt) {
        adminService.setAuthToken(jwt);
      }

      console.log('📝 Submitting profile for JIT user:', email);
      console.log('📝 EULA version being sent:', eulaVersion);

      if (!eulaVersion) {
        console.error('❌ CRITICAL: eulaVersion is empty/undefined! This will cause EULA to show again on reload.');
        setError('Unable to save EULA acceptance. Please go back and accept the EULA again.');
        setIsSubmitting(false);
        return;
      }

      await adminService.completeProfile({
        email: email,
        first_name: firstName,
        last_name: lastName,
        phone: phone || '',
        eula_version: eulaVersion
      });

      console.log('✅ Profile submitted, awaiting approval');
      onComplete();
    } catch (err) {
      console.error('❌ Failed to submit profile:', err);
      setError(err.message || 'Failed to submit profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await stytch.session.revoke();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">LUMOZ AI</h1>
          <p className="text-text-secondary">Complete Your Profile</p>
        </div>

        {/* Form */}
        <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6">
          <div className="mb-6">
            <p className="text-text-secondary text-sm">
              Welcome! Please complete your profile to request access.
              An administrator will review your request.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email || ''}
                disabled
                className="w-full px-3 py-2 bg-background-primary/50 border border-border-subtle rounded text-text-secondary cursor-not-allowed"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                First Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 bg-background-primary border border-border-subtle rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                placeholder="Enter your first name"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Last Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 bg-background-primary border border-border-subtle rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                placeholder="Enter your last name"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Phone (optional) */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Phone Number <span className="text-text-secondary text-xs">(optional)</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 bg-background-primary border border-border-subtle rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                placeholder="Enter your phone number"
                disabled={isSubmitting}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !firstName || !lastName}
              className="w-full bg-accent-primary-base text-text-inverse py-2 px-6 rounded-full font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Request Access'}
            </button>
          </form>

          {/* Logout option */}
          <div className="mt-6 pt-4 border-t border-border-subtle">
            <p className="text-text-secondary text-sm text-center mb-2">
              Wrong account?
            </p>
            <button
              onClick={handleLogout}
              className="w-full text-accent-lavender hover:text-accent-lavender/80 text-sm transition-colors"
            >
              Sign out and use a different email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewUserOnboarding;
